/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { THit } from '@com/core/common/types/catalog';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import ProductBox from '../listing/ProductBoxPLPDesktop';
import Subscription from '../detail/SubscribeOrSchedule/Subscription';

type TProps = {
    eventLocation: 'page_static' | 'recos_page-detail' | 'recos_page-static';
    products: THit[];
    isRecommended?: boolean;
};

const HorizontalProductBoxCarousel = ({ eventLocation, isRecommended = false, products }: TProps): JSX.Element => {
    SwiperCore.use([Navigation]);
    SwiperCore.use([Pagination]);
    const [titleWidth, setTitleWidth] = useState<number>(160);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState<boolean>(false);
    const [subscriptionProduct, setSubscriptionProduct] = useState<THit | null>();
    const [productSkuConfig, setProductSkuConfig] = useState<string>('');

    useEffect(() => {
        const actualWidth = document.getElementById('productContainer').clientWidth;
        setTitleWidth(actualWidth);
    }, []);

    return (
        <ProductBoxCarousel isRecommended={isRecommended}>
            <div className="siteWidthContainer">
                <Swiper
                    direction="horizontal"
                    loop={false}
                    grabCursor
                    spaceBetween={16}
                    slidesPerView={5}
                    slidesPerGroup={5}
                    breakpoints={{
                        992: {
                            slidesPerView: 5,
                            slidesPerGroup: 5,
                        },
                        1200: {
                            slidesPerView: 6,
                            slidesPerGroup: 6,
                        },
                        1366: {
                            slidesPerView: isRecommended ? 8 : 7,
                            slidesPerGroup: isRecommended ? 8 : 7,
                            spaceBetween: 24,
                        },
                    }}
                    pagination={{
                        el: '.swiper-pagination',
                        clickable: true,
                    }}
                    navigation={{
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    }}>
                    {products.map((product: THit, i: number) => {
                        return (
                            <>
                                <SwiperSlide id="productContainer" key={`swiper-${i + 1}`}>
                                    <ProductBox
                                        isSubscriptionModal={showSubscriptionModal}
                                        showSubscriptionModal={(show, prodSkuconfig) => {
                                            setShowSubscriptionModal(!showSubscriptionModal);
                                            setSubscriptionProduct(product);
                                            setProductSkuConfig(prodSkuconfig);
                                        }}
                                        isRecommended={isRecommended}
                                        titleWidth={titleWidth}
                                        eventLocation={eventLocation}
                                        product={product}
                                    />
                                </SwiperSlide>
                            </>
                        );
                    })}
                    <div className="swiperControlsContainer">
                        <div className="swiperControls">
                            <div className="swiper-button-prev custom-navigation" />
                            <div className="swiper-pagination" />
                            <div className="swiper-button-next custom-navigation" />
                        </div>
                    </div>
                </Swiper>
            </div>
            {subscriptionProduct && subscriptionProduct.sku_config === productSkuConfig && (
                <Subscription
                    closeModal={() => {
                        setProductSkuConfig('');
                        setShowSubscriptionModal(false);
                        setSubscriptionProduct(null);
                    }}
                    product={subscriptionProduct}
                    skuConfig={subscriptionProduct?.sku_config}
                    offerCode={subscriptionProduct?.offer_code}
                    showSubscriptionBox={false}
                    subscriptionModalView="subscribe"
                />
            )}
        </ProductBoxCarousel>
    );
};

const ProductBoxCarousel = styled.div<{ isRecommended: boolean }>`
    .swiper-slide {
        height: auto; /* let flex define the height via stretch */
    }

    .swiperControlsContainer {
        text-align: center;
    }

    .swiperControls {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin: 8px 0 32px;
        background-color: ${({ isRecommended, theme }): string =>
            isRecommended ? theme.colors.bgPrimary : theme.colors.brandPrimaryLight};
        border-radius: 20px;
        padding: 4px;

        .swiper-button-next:hover,
        .swiper-button-prev:hover {
            background-color: ${({ theme }): string => theme.colors.brandPrimaryLight};
        }
    }

    .custom-navigation {
        margin-top: 0;
        position: static;
        height: 32px;
        width: 32px;
        border-radius: 100%;
        background-color: ${({ theme }): string => theme.colors.bgPrimary};

        &:after {
            font-size: 12px !important;
            color: ${({ theme }): string => theme.colors.body} !important;
            text-align: center !important;
        }

        &:hover {
            background-color: transparent;
        }
    }

    .swiper-pagination {
        position: static;
        margin: 0 20px;
    }

    .swiper-pagination-bullet {
        margin: 0 6px;
        opacity: 1;
        background: ${({ isRecommended, theme }): string =>
            isRecommended ? theme.colors.brandPrimaryLight : theme.colors.bgPrimary};
    }

    .swiper-pagination-bullet-active {
        background: ${({ theme }): string => theme.colors.brandPrimary};
    }
`;

export default React.memo(HorizontalProductBoxCarousel);
